import { useEffect, useMemo, useState } from "react";

import { api, ApiError } from "../lib/api";
import { useLive } from "../lib/use-live";
import { useT } from "../context/locale";
import { useToast } from "../context/toast";
import { Badge, Button, ErrorNote, Panel } from "../components/ui";
import { Toggle } from "../components/ui/form";
import type { PermissionAction, PermissionCatalogue, PermissionGrid } from "../types/api";
import type { TranslationKey } from "../i18n";

const ACTION_ORDER: PermissionAction[] = ["view", "create", "update", "delete"];

/**
 * Permissions — what each role is allowed to do.
 *
 * Superadmin only, and the two things it cannot touch are the point: a
 * superadmin's own permissions are not stored and not listed, and neither
 * managing administrators nor this screen is a module. A grid that could grant
 * the right to edit the grid would be a grid one role could use to promote
 * itself, and there would be no way back from a save that switched everything
 * off.
 *
 * Read-only until Edit, like Notifications and for the same reason: every panel
 * here is a live subscription, and a form reseeded from each answer would
 * discard whatever was half-typed when one arrived.
 */
export function Permissions() {
  const t = useT();
  const toast = useToast();
  const catalogue = useLive<PermissionCatalogue>("permissions", {});

  const [role, setRole] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<PermissionGrid>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const roles = useMemo(() => catalogue.data?.roles ?? [], [catalogue.data]);
  const modules = catalogue.data?.modules ?? {};
  const active = role ?? roles[0] ?? null;

  // Follow the server, except while an edit is open — see the note above.
  useEffect(() => {
    if (editing || !catalogue.data || !active) return;
    setDraft(structuredClone(catalogue.data.grids[active] ?? {}));
  }, [catalogue.data, active, editing]);

  const stored = active ? catalogue.data?.grids[active] : undefined;
  const defaults = active ? catalogue.data?.defaults[active] : undefined;

  /** True when the draft says something different from what is saved. */
  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(stored ?? {}),
    [draft, stored],
  );

  function toggle(module: string, action: PermissionAction, next: boolean) {
    setDraft((current) => ({
      ...current,
      [module]: { ...current[module], [action]: next },
    }));
  }

  function switchRole(next: string) {
    // Changing role mid-edit would silently throw the draft away.
    if (editing && dirty && !window.confirm(t("permissions.discardPrompt"))) return;
    setEditing(false);
    setError(null);
    setRole(next);
  }

  function cancel() {
    setEditing(false);
    setError(null);
    if (stored) setDraft(structuredClone(stored));
  }

  async function save() {
    if (!active) return;
    setBusy(true);
    setError(null);
    try {
      await api.updatePermissions(active, draft);
      toast.success(
        t("permissions.saved"),
        t("permissions.savedDetail", {
          role: t(`admins.role.${active}` as TranslationKey),
        }),
      );
      setEditing(false);
      catalogue.reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t("error.save"));
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    if (!active || !window.confirm(t("permissions.resetPrompt"))) return;
    setBusy(true);
    setError(null);
    try {
      await api.resetPermissions(active);
      toast.success(t("permissions.reset"), t("permissions.resetDetail"));
      setEditing(false);
      catalogue.reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t("error.save"));
    } finally {
      setBusy(false);
    }
  }

  if (catalogue.error) {
    return <ErrorNote message={catalogue.error} onRetry={catalogue.reload} />;
  }

  return (
    <div className="max-w-4xl space-y-5">
      <div
        className="panel px-5 py-3"
        style={{ borderLeft: "3px solid var(--accent)" }}
      >
        <p className="text-muted text-[12px]">{t("permissions.note")}</p>
        <p className="text-dim mt-1 text-[12px]">{t("permissions.superadminNote")}</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {roles.map((name) => (
            <Button
              key={name}
              size="sm"
              variant={name === active ? "gold" : "ghost"}
              onClick={() => switchRole(name)}
            >
              {t(`admins.role.${name}` as TranslationKey)}
            </Button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Badge tone={editing ? "gold" : "neutral"}>
            {editing ? t("permissions.editing") : t("permissions.live")}
          </Badge>
          {editing ? (
            <>
              <Button variant="ghost" onClick={cancel} disabled={busy}>
                {t("action.cancel")}
              </Button>
              <Button variant="gold" onClick={save} loading={busy} disabled={!dirty}>
                {t("action.save")}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={reset} disabled={busy}>
                {t("permissions.resetAction")}
              </Button>
              <Button onClick={() => setEditing(true)}>{t("action.edit")}</Button>
            </>
          )}
        </div>
      </div>

      {Object.entries(modules).map(([module, definition]) => (
        <Panel key={module} eyebrow={t("permissions.module")} title={definition.label}>
          <div className="space-y-3">
            {ACTION_ORDER.filter((action) => definition.actions[action]).map((action) => {
              const on = Boolean(draft[module]?.[action]);
              const shipped = Boolean(defaults?.[module]?.[action]);
              return (
                <div key={action} className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold">
                      {t(`permissions.action.${action}` as TranslationKey)}
                      {/* What the switch actually does, in words. A grid of bare
                          CRUD verbs is how somebody grants "delete" on credit
                          without realising it means taking a player's money. */}
                      {on !== shipped ? (
                        <span className="text-dim ml-2 text-[11px] font-normal">
                          {t("permissions.changedFromDefault")}
                        </span>
                      ) : null}
                    </p>
                    <p className="text-muted text-[12px]">{definition.actions[action]}</p>
                  </div>
                  <Toggle
                    label=""
                    checked={on}
                    onChange={(next) => toggle(module, action, next)}
                    disabled={!editing}
                  />
                </div>
              );
            })}
          </div>
        </Panel>
      ))}

      {error ? <ErrorNote message={error} /> : null}
    </div>
  );
}
